import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  Clock,
  BarChart,
  Users,
  CheckCircle,
  Play,
  Award,
  Calendar,
  ArrowLeft,
} from "lucide-react";
import axios from "axios";

const CourseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await axios.get(`${API}/courses/${id}`);
        setCourse(response.data);
      } catch (error) {
        console.error("Error fetching course:", error);
        toast.error("Course not found");
        navigate("/courses");
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [id, navigate]);

  const handleEnroll = async () => {
    if (!user) {
      toast.error("Please login to enroll");
      navigate("/login");
      return;
    }

    setEnrolling(true);
    try {
      await axios.post(
        `${API}/courses/enroll`,
        { course_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Successfully enrolled! Check your dashboard.");
      navigate("/dashboard");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("You're already enrolled in this course");
      } else {
        toast.error("Failed to enroll. Please try again.");
      }
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </Layout>
    );
  }

  if (!course) return null;

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-slate-900 text-white py-16" data-testid="course-hero">
        <div className="container-padding">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Courses
          </button>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8 space-y-6">
              <span className="badge badge-lime">{course.category}</span>
              <h1 className="font-heading text-4xl lg:text-5xl font-light">{course.title}</h1>
              <p className="text-slate-300 text-lg">{course.description}</p>
              <div className="flex flex-wrap items-center gap-6 text-slate-300">
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {course.duration}
                </span>
                <span className="flex items-center gap-2">
                  <BarChart className="w-5 h-5" />
                  {course.level}
                </span>
                <span className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  {course.enrolled_count?.toLocaleString()} enrolled
                </span>
              </div>
              <div className="flex items-center gap-4 pt-4">
                <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center text-lg font-medium">
                  {course.instructor.charAt(0)}
                </div>
                <div>
                  <p className="font-medium">{course.instructor}</p>
                  <p className="text-sm text-slate-400">{course.instructor_bio}</p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="bg-white rounded-2xl p-8 text-slate-900 sticky top-28">
                <img
                  src={course.image_url}
                  alt={course.title}
                  className="w-full h-48 object-cover rounded-xl mb-6"
                />
                <div className="text-4xl font-bold mb-6">${course.price}</div>
                <Button
                  onClick={handleEnroll}
                  disabled={enrolling}
                  className="w-full bg-lime-500 text-slate-900 hover:bg-lime-400 rounded-full py-6 text-lg font-medium"
                  data-testid="enroll-btn"
                >
                  {enrolling ? "Processing..." : "Enroll Now"}
                </Button>
                <p className="text-center text-sm text-slate-500 mt-4">
                  30-day money-back guarantee
                </p>
                <div className="mt-6 pt-6 border-t border-slate-100 space-y-4">
                  {course.features?.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-lime-500" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="section-padding" data-testid="curriculum-section">
        <div className="container-padding">
          <div className="max-w-4xl">
            <h2 className="heading-2 mb-8">Course Curriculum</h2>
            <div className="space-y-4">
              {course.curriculum?.map((module, index) => (
                <div
                  key={index}
                  className="border border-slate-100 rounded-xl overflow-hidden"
                  data-testid={`curriculum-module-${index}`}
                >
                  <div className="p-6 bg-slate-50 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-medium">
                        {module.week || index + 1}
                      </div>
                      <div>
                        <h3 className="font-medium text-slate-900">{module.title}</h3>
                      </div>
                    </div>
                    <Play className="w-5 h-5 text-slate-400" />
                  </div>
                  {module.topics && (
                    <div className="p-6 space-y-3">
                      {module.topics.map((topic, topicIndex) => (
                        <div key={topicIndex} className="flex items-center gap-3 text-slate-600">
                          <CheckCircle className="w-4 h-4 text-lime-500" />
                          <span>{topic}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Instructor */}
      <section className="section-padding bg-slate-50" data-testid="instructor-section">
        <div className="container-padding">
          <div className="max-w-4xl">
            <h2 className="heading-2 mb-8">About the Instructor</h2>
            <div className="bg-white rounded-2xl p-8 flex flex-col md:flex-row gap-8">
              <div className="w-32 h-32 rounded-2xl bg-slate-200 flex items-center justify-center text-4xl font-medium text-slate-600 flex-shrink-0">
                {course.instructor.charAt(0)}
              </div>
              <div>
                <h3 className="font-heading text-2xl font-medium text-slate-900 mb-2">
                  {course.instructor}
                </h3>
                <p className="text-slate-600 mb-4">{course.instructor_bio}</p>
                <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Industry Expert
                  </span>
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    10,000+ Students
                  </span>
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    5+ Years Teaching
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CourseDetailPage;
